// 用包内核心 TersaModel（无 UI 依赖）：图像/视频注册表来自 @flowmuse/models，
// 主站增强版（带 icon）在结构上兼容核心版，本文件只读数据字段。
import type { TersaModel } from '@flowmuse/models/providers';
import { pickLocalized } from '@/lib/i18n/pick-localized';

export const PLAN_DISPLAY: Record<string, string> = {
  starter: 'Starter',
  pro: 'Pro',
  max: 'Max',
  ultra: 'Ultra',
};

export const INTERVAL_LABEL: Record<string, string> = {
  year: 'Yearly',
  month: 'Monthly',
};

export const TAG_DISPLAY: Record<string, string> = {
  beta: 'Beta',
  alpha: 'Alpha',
  internal: 'Internal',
};

export function getModelDisabled(model: TersaModel, isSubscribed: boolean) {
  if ((model as any)._accessDenied) return true;
  if (model.disabled) return true;
  if ((model as any)._hasModelPass) return false;
  if (!isSubscribed && (model.priceIndicator === 'highest' || model.priceIndicator === 'high')) return true;
  return false;
}

/**
 * 在不隐藏模型的前提下，给不支持当前编辑场景（图生图 i2i / 视频转视频 v2v）的
 * 模型打上 `_modeIncompatible` 标记，由 ModelSelector 渲染提示并在选择时 toast。
 *
 * 兼容模型（`supportsEdit === true`）保持原引用，仅对不兼容模型做浅拷贝，
 * 避免无谓的对象分配与下游重渲染。
 */
export function markIncompatibleModels<T extends Record<string, TersaModel>>(
  models: T,
  tag: 'i2i' | 'v2v',
): T {
  return Object.fromEntries(
    Object.entries(models).map(([k, m]) =>
      (m as any).supportsEdit ? [k, m] : [k, { ...(m as any), _modeIncompatible: tag }],
    ),
  ) as T;
}

/**
 * 模型规则是否只设置了 tag 准入（无 plan 升级路径）。
 * 用于 model-selector / canvas-center 决定是显示「需要相应权限」toast
 * 还是打开订阅升级 modal。
 */
export function isTagOnlyDenied(model: TersaModel): boolean {
  const m = model as any;
  if (!m._accessDenied) return false;
  const plan = m._requiredPlan as string | undefined;
  const tags = m._requiredTags as string[] | undefined;
  return (!plan || plan === '*') && !!tags && tags.length > 0;
}

function buildPlanText(
  requiredPlan: string | undefined,
  requiredInterval: string | undefined,
  t: (key: string) => string,
): string {
  if (!requiredPlan || requiredPlan === '*') return '';
  const interval = requiredInterval && requiredInterval !== '*'
    ? `${t(INTERVAL_LABEL[requiredInterval] || requiredInterval)} `
    : '';
  return `${interval}${PLAN_DISPLAY[requiredPlan] || requiredPlan}+`;
}

function buildTagText(
  requiredTags: string[] | undefined,
  accessLabel: string | undefined,
): string {
  if (!requiredTags || requiredTags.length === 0) return '';
  if (accessLabel) return accessLabel;
  return requiredTags.map((tag) => TAG_DISPLAY[tag] || tag).join(' / ');
}

/**
 * 是否为该模型配置了自定义准入文案（accessLabels）。
 * 自定义文案（如「即将上线」）应原样展示，不再套「需要 xx 权限」模板。
 */
export function hasCustomAccessLabel(model: TersaModel, language?: string): boolean {
  const m = model as any;
  return pickLocalized('', m._accessLabels as Record<string, string> | undefined, language).length > 0;
}

/**
 * 生成被拒模型的访问要求文案。在 OR 准入策略下，若同时存在 plan 与 tag 要求，
 * 用 " / " 拼接两条解锁路径，例如 `Pro+ / Beta`。
 *
 * @param language 可选当前 i18n 语言；提供后会按 `_accessLabels` 多语言映射挑选
 *                 文案，未命中再回退到 requiredTags 名。
 */
export function getModelAccessLabel(
  model: TersaModel,
  t: (key: string) => string,
  language?: string,
): string {
  const m = model as any;
  if (m._hasModelPass) return t('Model pass available');
  if (!m._accessDenied) return t('Available');

  const localizedAccessLabel = pickLocalized(
    '',
    m._accessLabels as Record<string, string> | undefined,
    language,
  );

  const planText = buildPlanText(m._requiredPlan, m._requiredInterval, t);
  const tagText = buildTagText(m._requiredTags, localizedAccessLabel || undefined);

  if (planText && tagText) return `${planText} / ${tagText}`;
  if (planText) return planText;
  if (tagText) return tagText;
  return t('No access');
}
